"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  isPullToRefreshGestureBlocked,
  isPullToRefreshLocked,
} from "@/lib/pull-to-refresh-lock";

const SHOW_THRESHOLD = 15;
const TRIGGER_THRESHOLD = 85;
const MAX_PULL = 140;
const LINEAR_ZONE = 70;
const DIRECTION_LOCK_THRESHOLD = 12;
const VERTICAL_LOCK_RATIO = 2;
const RELEASE_EASING = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
const RELEASE_DURATION_MS = 480;
/** Tolerância de scroll no topo (iOS/PWA pode reportar 1–2px fora de zero). */
const SCROLL_TOP_TOLERANCE_PX = 3;

function applyTension(dy: number): number {
  if (dy <= LINEAR_ZONE) {
    return dy * 0.95;
  }
  const linear = LINEAR_ZONE * 0.95;
  const extra = dy - LINEAR_ZONE;
  const resistance = MAX_PULL * (1 - Math.exp((-0.4 * extra) / MAX_PULL));
  return Math.min(linear + resistance, MAX_PULL);
}

type UsePullToRefreshOptions = {
  onRefresh: () => void | Promise<void>;
  enabled?: boolean;
};

export function usePullToRefresh({
  onRefresh,
  enabled = true,
}: UsePullToRefreshOptions) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const pullDistanceRef = useRef(0);
  const gestureLockRef = useRef<"vertical" | "horizontal" | null>(null);
  const gestureIgnoredRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  const resetIndicator = useCallback(() => {
    if (isRefreshingRef.current) return;

    const indicator = indicatorRef.current;
    const icon = iconRef.current;
    if (!indicator) return;

    indicator.style.opacity = "0";
    indicator.style.transition = "opacity 250ms ease-out";
    indicator.setAttribute("aria-hidden", "true");

    if (icon) {
      icon.style.transform = "scale(0.3)";
      icon.style.transition = "transform 250ms ease-out";
    }
  }, []);

  const showLoadingIndicator = useCallback(() => {
    const indicator = indicatorRef.current;
    if (!indicator) return;

    indicator.style.opacity = "1";
    indicator.style.transition = "none";
    indicator.setAttribute("aria-hidden", "false");
  }, []);

  const runRefresh = useCallback(async () => {
    isRefreshingRef.current = true;
    setIsRefreshing(true);
    showLoadingIndicator();

    try {
      await onRefreshRef.current();
    } catch {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
      resetIndicator();
    }
  }, [resetIndicator, showLoadingIndicator]);

  const applyContentTransform = useCallback((px: number) => {
    const content = contentRef.current;
    if (!content) return;
    content.style.transform = `translate3d(0, ${px}px, 0)`;
    content.style.transition = px > 0 ? "none" : "";
  }, []);

  const applyIndicatorTransform = useCallback((distance: number) => {
    if (isRefreshingRef.current) return;

    const indicator = indicatorRef.current;
    const icon = iconRef.current;
    if (!indicator) return;

    const opacity = Math.min(1, (distance / 45) * 1.1);
    const scale = Math.min(1, 0.3 + (distance / 100) * 0.7);

    indicator.style.opacity = String(opacity);
    indicator.style.transition = "none";
    indicator.setAttribute("aria-hidden", "false");

    if (icon) {
      icon.style.transform = `scale(${scale})`;
      icon.style.transition = "none";
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const content = contentRef.current;
    if (content) {
      content.style.transform = "";
      content.style.transition = "";
    }

    const indicator = indicatorRef.current;
    if (indicator && !isRefreshingRef.current) {
      indicator.style.opacity = "0";
      indicator.setAttribute("aria-hidden", "true");
    }

    const initialRef = { x: 0, y: 0 };

    const getScrollTop = () =>
      window.scrollY ??
      document.documentElement.scrollTop ??
      document.body.scrollTop ??
      0;

    const isAtScrollTop = () => getScrollTop() <= SCROLL_TOP_TOLERANCE_PX;

    const handleTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return;

      gestureIgnoredRef.current = isPullToRefreshGestureBlocked(e.target);
      if (gestureIgnoredRef.current) return;

      if (!isAtScrollTop()) return;

      initialRef.x = e.touches[0].clientX;
      initialRef.y = e.touches[0].clientY;
      gestureLockRef.current = null;
      pullDistanceRef.current = 0;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isRefreshingRef.current) return;

      if (gestureIgnoredRef.current || isPullToRefreshLocked()) {
        return;
      }

      if (!isAtScrollTop()) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const dx = currentX - initialRef.x;
      const dy = currentY - initialRef.y;

      if (!gestureLockRef.current) {
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < DIRECTION_LOCK_THRESHOLD) return;

        if (Math.abs(dx) >= Math.abs(dy)) {
          gestureLockRef.current = "horizontal";
        } else if (Math.abs(dy) > Math.abs(dx) * VERTICAL_LOCK_RATIO) {
          gestureLockRef.current = "vertical";
        } else {
          return;
        }
      }

      if (gestureLockRef.current === "horizontal") return;

      if (dy > 0 && dy >= SHOW_THRESHOLD) {
        e.preventDefault();

        if (contentRef.current && !contentRef.current.style.willChange) {
          contentRef.current.style.willChange = "transform";
        }

        const tensioned = applyTension(dy);
        pullDistanceRef.current = tensioned;
        applyContentTransform(tensioned);
        applyIndicatorTransform(tensioned);
      }
    };

    const handleTouchEnd = async () => {
      if (isRefreshingRef.current) return;

      if (gestureIgnoredRef.current) {
        gestureIgnoredRef.current = false;
        gestureLockRef.current = null;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        if (contentRef.current) {
          contentRef.current.style.willChange = "";
        }
        return;
      }

      const lockedDirection = gestureLockRef.current;
      gestureLockRef.current = null;

      const dy = pullDistanceRef.current;
      pullDistanceRef.current = 0;
      setPullDistance(0);

      if (lockedDirection !== "vertical" || dy === 0) {
        if (contentRef.current) {
          contentRef.current.style.willChange = "";
        }
        return;
      }

      const willRefresh = dy >= TRIGGER_THRESHOLD;
      const currentContent = contentRef.current;
      if (!currentContent) return;

      currentContent.style.transition = `transform ${RELEASE_DURATION_MS}ms ${RELEASE_EASING}`;
      currentContent.style.transform = "translate3d(0, 0, 0)";

      if (willRefresh) {
        await new Promise<void>((resolve) => {
          const done = () => {
            currentContent.removeEventListener("transitionend", done);
            resolve();
          };
          currentContent.addEventListener("transitionend", done);
        });

        await runRefresh();
      } else {
        resetIndicator();
      }

      const onTransitionEnd = () => {
        currentContent.removeEventListener("transitionend", onTransitionEnd);
        currentContent.style.transition = "";
        currentContent.style.willChange = "";
      };
      currentContent.addEventListener("transitionend", onTransitionEnd);
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove, true);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    enabled,
    applyContentTransform,
    applyIndicatorTransform,
    resetIndicator,
    runRefresh,
  ]);

  const canTrigger = pullDistance >= TRIGGER_THRESHOLD;
  const showIndicator = pullDistance >= SHOW_THRESHOLD;

  return {
    contentRef,
    indicatorRef,
    iconRef,
    pullDistance,
    showIndicator,
    canTrigger,
    isRefreshing,
  };
}
